import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, RefreshCw, Play, ArrowRight, Copy, Clock, Image, Type, FileSignature } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import api from "../lib/api";


const initialFormState = {
  name: '',
  source_channel_id: '',
  source_channel_name: '',
  target_channels: [],
  copy_media: true,
  copy_text: true,
  add_signature: false,
  signature_text: '',
  delay_seconds: 0,
};

export default function AutoRepost() {
  const [reposts, setReposts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRepost, setEditingRepost] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [newTargetId, setNewTargetId] = useState('');
  const [newTargetName, setNewTargetName] = useState('');

  const fetchReposts = async () => {
    try {
      const res = await api.get(`/auto-reposts`);
      setReposts(res.data);
    } catch (error) {
      toast.error('Ошибка загрузки правил авторепоста');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReposts();
  }, []);

  const openCreateModal = () => {
    setEditingRepost(null);
    setFormData(initialFormState);
    setModalOpen(true);
  };

  const openEditModal = (repost) => {
    setEditingRepost(repost);
    setFormData({
      name: repost.name,
      source_channel_id: repost.source_channel_id,
      source_channel_name: repost.source_channel_name || '',
      target_channels: repost.target_channels || [],
      copy_media: repost.copy_media,
      copy_text: repost.copy_text,
      add_signature: repost.add_signature,
      signature_text: repost.signature_text || '',
      delay_seconds: repost.delay_seconds || 0,
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.target_channels.length === 0) {
      toast.error('Добавьте хотя бы один целевой канал');
      return;
    }
    setSubmitting(true);

    try {
      if (editingRepost) {
        await api.put(`/auto-reposts/${editingRepost.id}`, formData);
        toast.success('Правило обновлено');
      } else {
        await api.post(`/auto-reposts`, formData);
        toast.success('Правило создано');
      }
      setModalOpen(false);
      fetchReposts();
    } catch (error) {
      toast.error('Ошибка сохранения');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (repost) => {
    if (!window.confirm(`Удалить правило "${repost.name}"?`)) return;

    try {
      await api.delete(`/auto-reposts/${repost.id}`);
      toast.success('Правило удалено');
      fetchReposts();
    } catch (error) {
      toast.error('Ошибка удаления');
    }
  };

  const handleToggleActive = async (repost) => {
    try {
      await api.put(`/auto-reposts/${repost.id}`, { is_active: !repost.is_active });
      fetchReposts();
      toast.success(repost.is_active ? 'Авторепост отключен' : 'Авторепост включен');
    } catch (error) {
      toast.error('Ошибка изменения статуса');
    }
  };

  const handleTestRepost = async (repost) => {
    try {
      const res = await api.post(`/auto-reposts/${repost.id}/test`);
      const results = res.data.results || [];
      const success = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;
      
      if (success > 0 && failed === 0) {
        toast.success(`Тестовое сообщение отправлено в ${success} каналов`);
      } else if (success > 0) {
        toast.warning(`Отправлено в ${success} каналов, ошибка в ${failed}`);
      } else {
        toast.error('Не удалось отправить тестовое сообщение');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка тестирования');
    }
  };

  const addTargetChannel = () => {
    if (!newTargetId.trim()) {
      toast.error('Введите ID канала');
      return;
    }
    
    const exists = formData.target_channels.some(c => c.id === newTargetId);
    if (exists) {
      toast.error('Канал уже добавлен');
      return;
    }
    
    setFormData({
      ...formData,
      target_channels: [
        ...formData.target_channels,
        { id: newTargetId.trim(), name: newTargetName.trim() || newTargetId.trim() }
      ]
    });
    setNewTargetId('');
    setNewTargetName('');
  };

  const removeTargetChannel = (channelId) => {
    setFormData({
      ...formData,
      target_channels: formData.target_channels.filter(c => c.id !== channelId)
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="auto-repost-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Авторепост</h1>
          <p className="text-zinc-500">Автоматическое копирование постов между каналами</p>
        </div>
        <Button 
          onClick={openCreateModal} 
          className="glow-primary"
          data-testid="add-repost-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Создать правило
        </Button>
      </div>

      {/* Info Card */}
      <div className="card bg-[#2AABEE]/5 border-[#2AABEE]/20">
        <div className="card-content">
          <h4 className="text-sm font-medium text-[#2AABEE] mb-2">Как работает авторепост</h4>
          <ul className="text-xs text-zinc-400 space-y-1">
            <li>• Бот должен быть админом в канале-источнике (для получения постов)</li>
            <li>• Бот должен быть админом в целевых каналах (с правом публикации)</li>
            <li>• Посты копируются без указания источника (идентичные)</li>
            <li>• Поддерживаются: текст, фото, видео, документы, аудио, голосовые, стикеры</li>
          </ul>
        </div>
      </div>

      {reposts.length > 0 ? (
        <div className="space-y-4">
          {reposts.map((repost) => (
            <div 
              key={repost.id} 
              className="card card-hover"
              data-testid={`repost-card-${repost.id}`}
            >
              <div className="card-content">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-lg font-medium text-white">{repost.name}</h3>
                      <Switch
                        checked={repost.is_active}
                        onCheckedChange={() => handleToggleActive(repost)}
                      />
                    </div>
                    
                    {/* Source -> Targets */}
                    <div className="flex items-center gap-2 flex-wrap mb-4">
                      <div className="px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                        <p className="text-xs text-emerald-400 font-medium">Источник</p>
                        <p className="text-sm text-white">{repost.source_channel_name || repost.source_channel_id}</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-zinc-600" />
                      <div className="flex items-center gap-2 flex-wrap">
                        {repost.target_channels?.map((target, idx) => (
                          <div key={idx} className="px-3 py-1.5 bg-[#2AABEE]/10 border border-[#2AABEE]/20 rounded-lg">
                            <p className="text-xs text-[#2AABEE] font-medium">Цель {idx + 1}</p>
                            <p className="text-sm text-white">{target.name || target.id}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Settings indicators */}
                    <div className="flex items-center gap-4 text-xs text-zinc-500">
                      <span className={`flex items-center gap-1 ${repost.copy_media ? 'text-emerald-400' : ''}`}>
                        <Image className="w-3 h-3" />
                        Медиа {repost.copy_media ? '✓' : '✗'}
                      </span>
                      <span className={`flex items-center gap-1 ${repost.copy_text ? 'text-emerald-400' : ''}`}>
                        <Type className="w-3 h-3" />
                        Текст {repost.copy_text ? '✓' : '✗'}
                      </span>
                      {repost.add_signature && (
                        <span className="flex items-center gap-1 text-purple-400">
                          <FileSignature className="w-3 h-3" />
                          Подпись
                        </span>
                      )}
                      {repost.delay_seconds > 0 && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Задержка {repost.delay_seconds}с
                        </span>
                      )}
                    </div>
                    
                    {/* Stats */}
                    <div className="flex items-center gap-4 mt-3 text-xs text-zinc-500">
                      <span>Репостов: <span className="text-white">{repost.repost_count || 0}</span></span>
                      {repost.last_repost_at && (
                        <span>Последний: <span className="text-white">{formatDate(repost.last_repost_at)}</span></span>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTestRepost(repost)}
                      data-testid={`test-repost-${repost.id}`}
                    >
                      <Play className="w-3 h-3 mr-1" />
                      Тест
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditModal(repost)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(repost)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card">
          <div className="empty-state">
            <Copy className="empty-state-icon" />
            <p className="mb-4">Правила авторепоста не созданы</p>
            <Button onClick={openCreateModal}>
              <Plus className="w-4 h-4 mr-2" />
              Создать первое правило
            </Button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingRepost ? 'Редактировать правило' : 'Создать правило авторепоста'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Название правила</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Репост из канала A в B и C"
                  required
                  data-testid="repost-name-input"
                />
              </div>

              {/* Source Channel */}
              <div className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                <h4 className="text-sm font-medium text-emerald-400 mb-3">Канал-источник</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="source_id">Telegram ID канала</Label>
                    <Input
                      id="source_id"
                      value={formData.source_channel_id}
                      onChange={(e) => setFormData({ ...formData, source_channel_id: e.target.value })}
                      placeholder="-1001234567890"
                      required
                      data-testid="source-channel-id"
                    />
                  </div>
                  <div>
                    <Label htmlFor="source_name">Название (для удобства)</Label>
                    <Input
                      id="source_name"
                      value={formData.source_channel_name}
                      onChange={(e) => setFormData({ ...formData, source_channel_name: e.target.value })}
                      placeholder="Мой канал"
                      data-testid="source-channel-name"
                    />
                  </div>
                </div>
              </div>

              {/* Target Channels */}
              <div className="p-4 rounded-lg bg-[#2AABEE]/5 border border-[#2AABEE]/20">
                <h4 className="text-sm font-medium text-[#2AABEE] mb-3">Целевые каналы</h4>
                
                {formData.target_channels.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {formData.target_channels.map((channel, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2">
                        <div>
                          <p className="text-sm text-white">{channel.name || channel.id}</p>
                          <p className="text-xs text-zinc-500 font-mono">{channel.id}</p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeTargetChannel(channel.id)}
                          className="h-8 w-8 text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="grid grid-cols-5 gap-2">
                  <div className="col-span-2">
                    <Input
                      value={newTargetId}
                      onChange={(e) => setNewTargetId(e.target.value)}
                      placeholder="-1001234567890"
                      data-testid="target-channel-id"
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      value={newTargetName}
                      onChange={(e) => setNewTargetName(e.target.value)}
                      placeholder="Название"
                    />
                  </div>
                  <Button type="button" onClick={addTargetChannel} variant="outline">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Options */}
              <div className="p-4 rounded-lg bg-white/5 border border-white/5">
                <h4 className="text-sm font-medium text-white mb-3">Настройки копирования</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white">Копировать медиа</p>
                      <p className="text-xs text-zinc-500">Фото, видео, документы, аудио</p>
                    </div>
                    <Switch
                      checked={formData.copy_media}
                      onCheckedChange={(checked) => setFormData({ ...formData, copy_media: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white">Копировать текст</p>
                      <p className="text-xs text-zinc-500">Текст сообщения и подписи к медиа</p>
                    </div>
                    <Switch
                      checked={formData.copy_text}
                      onCheckedChange={(checked) => setFormData({ ...formData, copy_text: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white">Добавлять подпись</p>
                      <p className="text-xs text-zinc-500">Добавить свой текст к каждому посту</p>
                    </div>
                    <Switch
                      checked={formData.add_signature}
                      onCheckedChange={(checked) => setFormData({ ...formData, add_signature: checked })}
                    />
                  </div>
                  {formData.add_signature && (
                    <div>
                      <Label htmlFor="signature">Текст подписи</Label>
                      <Textarea
                        id="signature"
                        value={formData.signature_text}
                        onChange={(e) => setFormData({ ...formData, signature_text: e.target.value })}
                        placeholder="Подписывайтесь на @channel"
                        rows={2}
                        className="bg-[#121212] border-white/10"
                      />
                    </div>
                  )}
                  <div>
                    <Label htmlFor="delay">Задержка перед репостом (секунды)</Label>
                    <Input
                      id="delay"
                      type="number"
                      min="0"
                      max="3600"
                      value={formData.delay_seconds}
                      onChange={(e) => setFormData({ ...formData, delay_seconds: parseInt(e.target.value) || 0 })}
                      className="w-32"
                    />
                    <p className="text-xs text-zinc-500 mt-1">0 = мгновенный репост</p>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                Отмена
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-repost-btn">
                {submitting ? 'Сохранение...' : 'Сохранить'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
